/**
 * Auto Balance Node - Player View
 *
 * Tactical roster reveal showing ELO-balanced team assignments.
 * Staggered player animations create a "deployment briefing" feel.
 * Supports 1v1 through 40v40, 2-10+ teams, party indicators.
 * Uses reusable PlayerCard, TeamHeader, rank icons, and party colors.
 */

import { motion } from "framer-motion";
import { Users } from "lucide-react";
import { useMemo } from "react";
import { cn } from "../../../lib/utils";
import { NodeCard } from "../../components/NodeCard";
import { NodeHeader } from "../../components/NodeHeader";
import { useCurrentPlayerId } from "../../hooks/useCurrentPlayerId";
import type { NodePlayerViewProps } from "../types";
import type { AutoBalanceState } from "./types";

export function AutoBalanceView({ nodeState, players }: NodePlayerViewProps<AutoBalanceState>) {
  const userId = useCurrentPlayerId();
  const state = nodeState.state as AutoBalanceState;
  const config = nodeState.config as { labels?: { title?: string; subtitle?: string } };
  const title = config.labels?.title ?? "Team Assignment";
  const subtitle = config.labels?.subtitle ?? "Teams balanced by rating";

  // Group userId->teamNum into teamNum->[userId, ...] for rendering
  const playersByTeam = useMemo(() => {
    const grouped: Record<number, string[]> = {};
    for (const [uid, teamNum] of Object.entries(state.teams)) {
      if (!grouped[teamNum]) grouped[teamNum] = [];
      grouped[teamNum].push(uid);
    }
    return grouped;
  }, [state.teams]);

  const teamEntries = useMemo(
    () => Object.entries(playersByTeam).map(([num, ids]) => [num, ids] as [string, string[]]),
    [playersByTeam],
  );
  const teamCount = teamEntries.length;

  // ELO delta: max team avg minus min team avg
  const avgElos = teamEntries.map(([num, ids]) => {
    const total = state.teamElos[Number(num)] ?? 0;
    return ids.length > 0 ? Math.round(total / ids.length) : 0;
  });
  const eloDelta = avgElos.length >= 2 ? Math.max(...avgElos) - Math.min(...avgElos) : 0;

  return (
    <NodeCard>
      <NodeHeader title={title} subtitle={subtitle} />

      {/* ELO delta pill */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.15 }}
        className="text-center mb-6"
      >
        <div className="inline-flex items-center gap-3 px-4 py-2 rounded-lg bg-surface-800/80 border border-surface-700/50">
          <span className="font-display text-xs @sm:text-sm font-semibold uppercase tracking-wider text-text-secondary">
            &Delta;
          </span>
          <span className="font-display text-2xl font-bold text-text-primary tabular-nums">
            {eloDelta}
          </span>
          <span className="text-xs uppercase tracking-wider text-text-secondary font-medium">
            ELO
          </span>
          <div className="w-px h-5 bg-surface-700/60" />
          <span className="font-display text-lg font-bold text-brand-400 tabular-nums">
            {teamCount}
          </span>
          <span className="text-xs uppercase tracking-wider text-text-secondary font-medium">
            {teamCount === 1 ? "team" : "teams"}
          </span>
        </div>
      </motion.div>

      {/* Team grid */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.4 }}
        className={cn(
          "grid gap-4 mb-6",
          teamCount === 2
            ? "@md:grid-cols-2"
            : teamCount <= 4
              ? "grid-cols-2"
              : "grid-cols-2 @md:grid-cols-3",
        )}
      >
        {teamEntries.map(([teamNumStr, memberIds]) => {
          const teamNum = Number(teamNumStr);
          const isMyTeam = memberIds.includes(userId || "");
          const totalElo = state.teamElos[teamNum] ?? 0;
          const avgElo = memberIds.length > 0 ? Math.round(totalElo / memberIds.length) : 0;
          const memberCount = memberIds.length;
          const myPartyId = players.find((p) => p.userId === userId)?.partyId;

          return (
            <div
              key={teamNum}
              className="rounded-lg bg-surface-800/60 border border-surface-700/40 overflow-hidden"
            >
              {/* Team header */}
              <div
                className={cn(
                  "px-3 py-2 border-b border-surface-700/40 flex items-center gap-2",
                  isMyTeam && "bg-brand-500/[0.04]",
                )}
              >
                <span className="font-display text-xs font-semibold uppercase tracking-wider text-text-secondary flex-1">
                  Team {teamNum + 1}
                </span>
                {isMyTeam && (
                  <span className="text-[10px] font-display font-semibold uppercase tracking-wider text-brand-400">
                    (you)
                  </span>
                )}
                <span className="text-xs text-surface-500">avg {avgElo}</span>
              </div>

              {/* Players */}
              <div className="divide-y divide-surface-700/20">
                {memberIds.map((uid) => {
                  const player = players.find((p) => p.userId === uid);
                  const isMe = uid === userId;
                  const isPartyMate =
                    !isMe && !!myPartyId && player?.partyId === myPartyId && isMyTeam;

                  return (
                    <div key={uid} className="flex items-center gap-2 px-3 py-2">
                      <span
                        className={cn(
                          "text-sm font-medium flex-1 truncate",
                          isMe ? "text-brand-300" : "text-text-primary",
                        )}
                      >
                        {player?.username ?? uid}
                      </span>
                      {isPartyMate && (
                        <Users
                          size={12}
                          className="text-brand-400/70 shrink-0"
                          aria-hidden="true"
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Footer */}
              <div className="px-3 py-2 bg-surface-900/40 border-t border-surface-700/40">
                <span className="text-xs text-surface-500">
                  {memberCount} {memberCount === 1 ? "player" : "players"}
                </span>
              </div>
            </div>
          );
        })}
      </motion.div>
    </NodeCard>
  );
}
